﻿import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';

import { FormSelect, FormSwitchList, FormTextInput } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { FormPasswordInput } from 'components/form-fields/FormPasswordInput';
import { useValidation } from "hooks/useValidation";
import { NotificationInline } from 'components/notifications';
import { Button } from 'components/buttons';

const defaultArray = [];
const defaultObject = {};

export function IntegrationSettings(
    {
        providers = defaultArray,
        settings = defaultObject,
        editing = false,
        saving = false,
        loading = true,
        token = null,
        onUpdate,
        onValidationUpdate
    }
) {

    const [isValid, errors, validate] = useValidation();

    const [selectedProvider, setSelectedProvider] = useState({
        providerId: null
    });

    const [providersValue, setProviders] = useState([]);
    const [services, setServices] = useState([]);

    useEffect(function () {

        setProviders(providers.map(function (provider) {
            return {
                ...provider,
                id: provider.providerId,
                name: provider.providerName
            };
        }));

    }, [providers]);

    useEffect(onValidationChange, [isValid]);

    function onValidationChange() {
        onValidationUpdate(isValid);
    }

    useEffect(updateValidationRules, [settings, selectedProvider]);

    function updateValidationRules() {

        const rules = [
            { fieldName: "services", required: true },
            { fieldName: "providerId", required: true }
        ];

        if (selectedProvider.authType === "User") {
            rules.push({ fieldName: "username", required: true });

            if (!editing) {
                rules.push({ fieldName: "password", required: true });
            }
        }

        if (selectedProvider.authType === "TokenCS") {
            rules.push({ fieldName: "username", required: true });
            rules.push({ fieldName: "password", required: true });
        }

        if ((selectedProvider.authType === "OAuth" || selectedProvider.authType === "Token") && !editing) {
            rules.push({ fieldName: "token", required: true });
        }

        if (selectedProvider.fleetNameRequired) {
            rules.push({ fieldName: "fleetName", required: true });
        }

        if (selectedProvider.additionalFields?.length > 0) {
            selectedProvider.additionalFields.forEach(field => {
                rules.push({
                    fieldName: `additionalFields.${field.integrationProviderAdditionalFieldId}`,
                    required: field.isMandatory && !editing
                });
            });
        }

        const flattenedSettings = {
            ...settings,
            additionalFields: (settings.additionalFields || []).reduce((acc, field) => {
                acc[field.integrationProviderAdditionalFieldId] = field.fieldValue;
                return acc;
            }, {})
        };

        validate(rules, flattenedSettings, []);
    }

    useEffect(function () {
        var foundProvider = providersValue.find(p => p.providerId === settings.providerId);
        setSelectedProvider(foundProvider != null ? foundProvider : {});
    }, [providersValue, settings.providerId]);

    useEffect(function () {
        if (selectedProvider.services != null) {

            var mappedServices = selectedProvider.services.map(function (service) {

                var isChecked = settings.services && settings.services.some(function (enabledService) {
                    return enabledService === service.id;
                });

                return {
                    ...service,
                    checked: isChecked
                }
            })

            setServices(mappedServices);
        }
    }, [selectedProvider.services, settings.services]);


    function onProviderUpdate(item) {
        onUpdate({
            ...settings,
            providerId: item.id
        });
    }

    function onUsernameUpdate(item) {
        onUpdate({
            ...settings,
            username: item.target.value
        });
    }

    function onPasswordUpdate(item) {
        onUpdate({
            ...settings,
            password: item.target.value
        });
    }

    function onTokenUpdate(item) {
        onUpdate({
            ...settings,
            token: item.target.value
        });
    }

    function onFleetNameUpdate(item) {
        onUpdate({
            ...settings,
            fleetName: item.target.value
        });
    }

    function onAdditionalFieldUpdate(event, integrationProviderAdditionalFieldId) {
        const fieldValue = event.target.value;

        const fieldMetadata = selectedProvider.additionalFields.find(
            field => field.integrationProviderAdditionalFieldId === integrationProviderAdditionalFieldId
        );

        const updatedAdditionalFields = settings.additionalFields?.filter(
            field => field.integrationProviderAdditionalFieldId !== integrationProviderAdditionalFieldId
        ) || [];

        updatedAdditionalFields.push({
            fieldValue,
            isMandatory: fieldMetadata?.isMandatory,
            integrationProviderId: selectedProvider.providerId,
            integrationProviderAdditionalFieldId: fieldMetadata.integrationProviderAdditionalFieldId
        });

        onUpdate({
            ...settings,
            additionalFields: updatedAdditionalFields
        });
    }

    function handleServiceChange(event) {
        var tempServices = [...settings.services];
        var toggleId = parseInt(event.currentTarget.getAttribute('identifier'));

        if (event.target.checked) {
            tempServices.push(toggleId);
        } else {
            tempServices = tempServices.filter(function (s) {
                return s !== toggleId;
            });
        }

        onUpdate({
            ...settings,
            services: tempServices
        });
    }

    function onOauthRedirect() {
        window.location = selectedProvider.authUrl;
    }

    return (
        <Fragment>
            <FormSelect
                disabled={saving || editing || (selectedProvider.authType === "OAuth" && token != null)}
                items={providersValue}
                onItemSelect={onProviderUpdate}
                placeholder="Select a provider"
                headingText="Select the provider you wish to connect to:"
                selectedValue={selectedProvider.providerId}
                dangerHelperText={""}
                loading={loading}
                id="input-integration-provider"
            ></FormSelect>
            <ShowHide
                evaluator={selectedProvider.providerId != null}
                show={(
                    <Fragment>

                        <ShowHide
                            evaluator={selectedProvider.authType === "User" || selectedProvider.authType === "TokenCS"}
                            show={(
                                <Fragment>
                                    <FormTextInput
                                        loading={loading}
                                        headingText={selectedProvider.authType === "User" ? "Username:" : "Client Id:"}
                                        value={settings.username}
                                        disabled={saving || editing}
                                        onChange={onUsernameUpdate}
                                        dangerHelperText={errors.username}
                                    />

                                    <NotificationInline show={editing && selectedProvider.authType === "User"} intent="info" text="Your password is only required if you wish to change it from the current password" />

                                    <FormPasswordInput
                                        loading={loading}
                                        headingText={selectedProvider.authType === "User" ? "Password:" : "Secret:"}
                                        value={settings.password}
                                        disabled={saving}
                                        onChange={onPasswordUpdate}
                                        dangerHelperText={errors.password}
                                    />
                                </Fragment>
                            )}
                        />

                        <ShowHide
                            evaluator={selectedProvider.authType === "Token"}
                            show={(
                                <Fragment>
                                    <NotificationInline show={editing} intent="info" text="Your token is only required if you wish to change it from the current token" />
                                    <FormPasswordInput
                                        loading={loading}
                                        headingText="Token:"
                                        value={settings.token}
                                        disabled={saving}
                                        onChange={onTokenUpdate}
                                        dangerHelperText={errors.token}
                                    />
                                </Fragment>
                            )}
                        />

                        <ShowHide
                            evaluator={selectedProvider.fleetNameRequired === true}
                            show={(
                                <FormTextInput
                                    loading={loading}
                                    headingText="Fleet name:"
                                    value={settings.fleetName}
                                    onChange={onFleetNameUpdate}
                                    dangerHelperText={errors.fleetName}
                                />
                            )}
                        />

                        <NotificationInline show={token != null} allowClose={false} intent="success" text="User credentials entered successfully, please select integration services to complete integration setup." />

                        <ShowHide
                            evaluator={(selectedProvider.additionalFields?.length || 0) > 0}
                            show={(
                                <Fragment>
                                    {selectedProvider.additionalFields?.map((field) => (
                                        <div key = {field.integrationProviderAdditionalFieldId}>
                                            <ShowHide
                                                evaluator={(!field.isVisible)}
                                                show={(
                                                    <NotificationInline show={editing} intent="info" text={`Your ${field.fieldName} is only required if you wish to change it from the current ${field.fieldName}`} />
                                                )}
                                            />
                                            <ShowHide
                                                evaluator={field.isVisible}
                                                show={
                                                    <FormTextInput
                                                        key={field.integrationProviderAdditionalFieldId}
                                                        loading={loading}
                                                        headingText={`${field.fieldName}:`}
                                                        value={
                                                            settings.additionalFields?.find(f => f.integrationProviderAdditionalFieldId === field.integrationProviderAdditionalFieldId)?.fieldValue || ""
                                                        }
                                                        disabled={saving}
                                                        onChange={(event) => onAdditionalFieldUpdate(event, field.integrationProviderAdditionalFieldId)}
                                                        dangerHelperText={errors[`additionalFields.${field.integrationProviderAdditionalFieldId}`]}
                                                    />
                                                }
                                                hide={
                                                    <FormPasswordInput
                                                        key={field.integrationProviderAdditionalFieldId}
                                                        loading={loading}
                                                        headingText={`${field.fieldName}:`}
                                                        value={
                                                            settings.additionalFields?.find(f => f.integrationProviderAdditionalFieldId === field.integrationProviderAdditionalFieldId)?.fieldValue || ""
                                                        }
                                                        disabled={saving}
                                                        onChange={(event) => onAdditionalFieldUpdate(event, field.integrationProviderAdditionalFieldId)}
                                                        dangerHelperText={errors[`additionalFields.${field.integrationProviderAdditionalFieldId}`]}
                                                        secureFieldName={field.fieldName}
                                                    />
                                                }
                                            />
                                        </div>
                                        
                                    ))}
                                </Fragment>
                            )}
                        />


                        <ShowHide
                            evaluator={selectedProvider.authType === "OAuth" && token == null}
                            show={(
                                <div className="spacer-bottom">
                                    <div className="spacer-bottom">
                                        <p>To authenticate with this provider, you will need to click the login link below. You will be taken to the provider's login page where you will need to enter your credentials. Upon successful login you will be redirected back here to complete the process.</p>
                                    </div>
                                    <Button text={`Login with ${selectedProvider.providerName}`} intent="primary" onClick={onOauthRedirect} />
                                </div>
                            )}
                            hide={(
                                <FormSwitchList
                                    id="integration-services"
                                    items={services}
                                    onChange={handleServiceChange}
                                    headingText="Integration Services:"
                                    helperText={"Select which services you would like the integration to collect data for"}
                                    loading={loading}
                                    switchListName="featureList"
                                    disabled={saving}
                                    dangerHelperText={errors.services}
                                />
                            )}
                        />
                        
                    </Fragment>
                )}
            />
        </Fragment>
    );
}


IntegrationSettings.propTypes = {
    providers: PropTypes.array,
    settings: PropTypes.object,
    onValidationUpdate: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    editing: PropTypes.bool,
    saving: PropTypes.bool,
    loading: PropTypes.bool,
    token: PropTypes.string
};